import { Ref, ref, nextTick } from '@vue/composition-api';

export interface StepperConfig {
	numSteps: number;
	initialStep: number;
	beforeNext(currentStep: number): void;
	afterNext(currentStep: number): void;
	beforePrevious(currentStep: number): void;
	afterPrevious(currentStep: number): void;
	beforeGoTo(currentStep: number): void;
	afterGoTo(currentStep: number): void;
}

// eslint-disable-next-line @typescript-eslint/no-empty-function
const voidFunc = () => {};

const defaultConfig: StepperConfig = {
	numSteps: 0,
	initialStep: 0,
	beforeNext: voidFunc,
	afterNext: voidFunc,
	beforePrevious: voidFunc,
	afterPrevious: voidFunc,
	beforeGoTo: voidFunc,
	afterGoTo: voidFunc,
};

export interface UseStepper {
	currentStep: Ref<number>;
	animationType: Ref<StepperAnimationType>;
	nextStep(): void;
	previousStep(): void;
	goToStep(val: number): void;
}

export enum StepperAnimationType {
	TO_LEFT = 'to-left',
	TO_RIGHT = 'to-right',
}

export const useStepper = (userConfig: Partial<StepperConfig>): UseStepper => {
	const config = { ...defaultConfig, ...userConfig };
	const currentStep = ref(config.initialStep);
	const animationType = ref(StepperAnimationType.TO_RIGHT);
	const nextStep = async () => {
		config.beforeNext(currentStep.value);
		animationType.value = StepperAnimationType.TO_LEFT;
		await nextTick();
		currentStep.value = Math.min(config.numSteps - 1, currentStep.value + 1);
		config.afterNext(currentStep.value);
	};
	const previousStep = async () => {
		config.beforePrevious(currentStep.value);
		animationType.value = StepperAnimationType.TO_RIGHT;
		await nextTick();
		currentStep.value = Math.max(0, currentStep.value - 1);
		config.afterPrevious(currentStep.value);
	};
	const goToStep = (val: number) => {
		config.beforeGoTo(currentStep.value);
		currentStep.value = Math.min(Math.max(0, val), config.numSteps - 1);
		config.afterGoTo(currentStep.value);
	};

	return {
		currentStep,
		animationType,
		nextStep,
		previousStep,
		goToStep,
	};
};
